/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * MedLinks - Mobile menu: scroll opened level-1 category to top of drawer,
 * then restore the pre-open scroll position when that category closes.
 */
(function () {
  const savedScrollByPanelId = new Map();
  let openCorrectionTimer = null;
  let restoreTimer = null;

  function getLevel1MenuContext(panel) {
    if (!(panel instanceof HTMLElement)) return null;
    if (!panel.matches("collapsible-content.collapsible")) return null;

    const drawer = panel.closest("#mobile-menu-drawer");
    if (!drawer) return null;

    const button = drawer.querySelector(
      `button[is="toggle-button"][aria-controls="${panel.id}"]`
    );
    if (!button) return null;

    const item = button.closest('.mobile-nav__item[data-level="1"]');
    if (!item || !item.contains(panel)) return null;

    const scrollContainer = drawer.querySelector(".drawer__content");
    if (!scrollContainer) return null;

    return { drawer, button, item, scrollContainer, panel };
  }

  function clearScrollTimers() {
    if (openCorrectionTimer) {
      clearTimeout(openCorrectionTimer);
      openCorrectionTimer = null;
    }
    if (restoreTimer) {
      clearTimeout(restoreTimer);
      restoreTimer = null;
    }
  }

  function scrollLevel1ItemToTop(panel) {
    const ctx = getLevel1MenuContext(panel);
    if (!ctx) return;

    // Only act when opening; ignore unrelated open events.
    if (ctx.button.getAttribute("aria-expanded") !== "true") return;

    clearScrollTimers();

    // Remember exact drawer position before we move this item to the top.
    savedScrollByPanelId.set(panel.id, ctx.scrollContainer.scrollTop);

    const scrollOpenedItemToTop = function () {
      const top =
        ctx.button.getBoundingClientRect().top -
        ctx.scrollContainer.getBoundingClientRect().top +
        ctx.scrollContainer.scrollTop;

      ctx.scrollContainer.scrollTo({
        top: Math.max(0, top),
        behavior: "smooth",
      });
    };

    // Smooth scroll once on open, then one late correction after expand
    // has progressed so the item stays aligned without abrupt jumps.
    scrollOpenedItemToTop();
    openCorrectionTimer = setTimeout(scrollOpenedItemToTop, 320);
  }

  function restoreLevel1Scroll(panel) {
    const ctx = getLevel1MenuContext(panel);
    if (!ctx) return;
    if (!savedScrollByPanelId.has(panel.id)) return;

    const savedScrollTop = savedScrollByPanelId.get(panel.id);
    savedScrollByPanelId.delete(panel.id);
    clearScrollTimers();

    const restoreScroll = function () {
      ctx.scrollContainer.scrollTo({
        top: Math.max(0, savedScrollTop),
        behavior: "smooth",
      });
    };

    // Smooth restore now, plus one correction after collapse progresses
    // so height animation cannot leave the drawer at the wrong spot.
    restoreScroll();
    restoreTimer = setTimeout(restoreScroll, 360);
  }

  // Capture phase: Focal's toggle-button stops propagation of these events.
  document.addEventListener(
    "openable-element:open",
    function (event) {
      scrollLevel1ItemToTop(event.target);
    },
    true
  );

  document.addEventListener(
    "openable-element:close",
    function (event) {
      restoreLevel1Scroll(event.target);
    },
    true
  );
})();

/**
 * MedLinks - Prevent the storefront behind the RevenueHunt link quiz from
 * scrolling while its full-screen popup is open. The current page position is
 * restored exactly when the popup closes (including iOS Safari).
 */
(function () {
  if (window.mlQuizScrollLockReady) return;
  window.mlQuizScrollLockReady = true;

  const triggerSelector = 'a[href="#quiz"]';
  const html = document.documentElement;
  const body = document.body;

  let locked = false;
  let popupSeen = false;
  let scrollY = 0;
  let monitorTimer = null;
  let launchTimeout = null;
  let originalStyles = null;

  function isVisible(element) {
    if (!(element instanceof HTMLElement)) return false;

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      Number(style.opacity || 1) > 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  function isViewportPopup(element) {
    let current = element;

    while (current && current !== body) {
      if (isVisible(current)) {
        const style = window.getComputedStyle(current);
        const rect = current.getBoundingClientRect();
        const coversViewport =
          rect.width >= window.innerWidth * 0.7 &&
          rect.height >= window.innerHeight * 0.7;

        if (coversViewport && (style.position === "fixed" || style.position === "absolute")) {
          return true;
        }
      }

      current = current.parentElement;
    }

    return false;
  }

  function hasVisibleQuizPopup() {
    const quizFrames = document.querySelectorAll(
      'iframe[src*="revenuehunt" i], iframe[src*="productquiz" i], iframe[title*="quiz" i]'
    );

    for (const frame of quizFrames) {
      if (isViewportPopup(frame)) return true;
    }

    const dialogs = document.querySelectorAll(
      '[role="dialog"], [aria-modal="true"], [id*="revenuehunt" i], [class*="revenuehunt" i], [id*="prq" i], [class*="prq" i], [id*="quiz-popup" i], [class*="quiz-popup" i]'
    );

    for (const dialog of dialogs) {
      if (!isViewportPopup(dialog)) continue;

      const identifier = `${dialog.id} ${dialog.className} ${dialog.getAttribute("aria-label") || ""}`;
      if (/quiz|revenuehunt|product.?recommendation/i.test(identifier)) return true;
    }

    return false;
  }

  function lockPageScroll() {
    if (locked) return;

    locked = true;
    popupSeen = false;
    scrollY = window.scrollY || window.pageYOffset || 0;

    originalStyles = {
      htmlOverflow: html.style.overflow,
      htmlOverscrollBehavior: html.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };

    html.classList.add("ml-quiz-scroll-locked");
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";

    monitorTimer = window.setInterval(checkQuizState, 200);

    // Avoid trapping the page if the app fails to launch the popup.
    launchTimeout = window.setTimeout(function () {
      if (!popupSeen) unlockPageScroll();
    }, 12000);
  }

  function unlockPageScroll() {
    if (!locked) return;

    locked = false;
    popupSeen = false;

    window.clearInterval(monitorTimer);
    window.clearTimeout(launchTimeout);
    monitorTimer = null;
    launchTimeout = null;

    html.classList.remove("ml-quiz-scroll-locked");
    html.style.overflow = originalStyles.htmlOverflow;
    html.style.overscrollBehavior = originalStyles.htmlOverscrollBehavior;
    body.style.position = originalStyles.bodyPosition;
    body.style.top = originalStyles.bodyTop;
    body.style.left = originalStyles.bodyLeft;
    body.style.right = originalStyles.bodyRight;
    body.style.width = originalStyles.bodyWidth;
    body.style.overflow = originalStyles.bodyOverflow;
    body.style.overscrollBehavior = originalStyles.bodyOverscrollBehavior;

    window.scrollTo(0, scrollY);
    originalStyles = null;
  }

  function checkQuizState() {
    const popupVisible = hasVisibleQuizPopup();

    if (popupVisible) {
      popupSeen = true;
      return;
    }

    if (popupSeen) unlockPageScroll();
  }

  document.addEventListener(
    "click",
    function (event) {
      if (!(event.target instanceof Element)) return;

      const trigger = event.target.closest(triggerSelector);
      if (!trigger) return;

      // Let RevenueHunt finish its own click handler before freezing the page.
      window.setTimeout(function () {
        lockPageScroll();
        window.setTimeout(checkQuizState, 0);
      }, 0);
    },
    true
  );

  window.addEventListener("pageshow", function () {
    if (locked) unlockPageScroll();
  });
})();
